import {
  ref,
  computed,
  readonly,
  type ComputedRef,
  type Ref,
  type UnwrapRef,
  type DeepReadonly,
} from '@/composition/vue/compositionApi'

export type AsyncStatus = 'pending' | 'error' | 'success' | 'notInit'

interface UseAsyncFuncStatus<T, TRun = void> {
  status: DeepReadonly<Ref<UnwrapRef<AsyncStatus>>>
  promise: DeepReadonly<Ref<UnwrapRef<Promise<T> | null>>>
  isPending: ComputedRef<boolean>
  isError: ComputedRef<boolean>
  isSuccess: ComputedRef<boolean>
  run(params: TRun): Promise<T>
}

export const useAsyncFuncStatus = <T, TRun = void>(
  callback: (params: TRun) => Promise<T>,
): UseAsyncFuncStatus<T, TRun> => {
  const _status = ref<AsyncStatus>('notInit')
  const _promise = ref<Promise<T> | null>(null)

  const status = readonly(_status)
  const promise = readonly(_promise)

  const isPending = computed(() => _status.value === 'pending')
  const isError = computed(() => _status.value === 'error')
  const isSuccess = computed(() => _status.value === 'success')

  let lastStartAt: number

  const run = async (params: TRun) => {
    const startAt = Date.now()
    lastStartAt = startAt
    _status.value = 'pending'
    try {
      _promise.value = callback(params)

      const res = await _promise.value

      if (lastStartAt === startAt) {
        _status.value = 'success'
      }

      return res
    } catch (e) {
      if (lastStartAt === startAt) {
        _status.value = 'error'
      }

      throw e
    } finally {
      _promise.value = null
    }
  }

  return {
    status,
    promise,
    isPending,
    isError,
    isSuccess,
    run,
  }
}
